import { computed, onMounted, ref } from "vue"
import GoggleMapsWithMarkers from "../../../../Frontend/src/components/GoogleMapsWithMarkers/GoogleMapsWithMarkers.vue"
import MultiSelectDropdown from "../../../../Frontend/src/components/MultiSelectDropdown/MultiSelectDropdown.vue"

import zoomMapToCenters from "./methods/zoomMapToCenters"
import setFocusPoints from "./methods/setFocusPoints"
import initGooglePlacesAutocomplete from "./methods/initGooglePlacesAutocomplete"
import mapCategoriesToMultiSelectOptions from "./methods/mapCategoriesToMultiSelectOptions"
import getDistanceFromLatLonInKm from "./methods/getDistanceFromLatLonInKm"

export default {
  name: "CenterMap",
  props: {
    categories: {
      type: Array,
    },
    labels: {
      type: Array,
    },
    mapkey: {
      type: String,
      required: true,
    },
    centerInformation: {
      type: Array,
      required: true,
    },
    bankTypes: {
      type: Array,
      required: true,
    }
  },
  setup(props) {
    const centerInformationComplete = ref(props.centerInformation)
    const centerInformationFiltered = ref(props.centerInformation) // map will display these locations
    const googlePlacesSearchField = ref(null)
    const placeSelected = ref(null)
    const focusPoints = ref([]) // map will zoom to these focus points
    const placeSearchQuery = ref("")
    const showMapsMobile = ref(true)
    const showListMobileCount = ref(3)
    const currentZoom = ref(7)

    const googlePlacesSearchResultsShown = ref(false)
    const googlePlacesSearchResultsRef = ref(null)

    const multiSelectCategoriesOptions = ref(mapCategoriesToMultiSelectOptions(props.categories))
    const selectedBanks = ref(['nykredit', 'sparnord'])
    const selectedCategories = ref([])

    // handling google places search results, to handle WCAG
    const googlePlacesSearchResultsMutationHandler = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes') {
          googlePlacesSearchResultsShown.value = googlePlacesSearchResultsRef.value.style.display !== 'none'

          if (mutation.attributeName === 'class' && mutation.target.classList.value.includes('pac-item-selected')) {
            mutation.target.setAttribute('aria-selected', 'true')
          }
        }

        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((item) => {
            item.setAttribute('aria-selected', 'false')
            observer.observe(item, { attributes: true })
          })
        }
      }
    }

    const observer = new MutationObserver(googlePlacesSearchResultsMutationHandler)

    const findGooglePlacesSearchResultsElement = () => {
      const searchResults = document.querySelector('.pac-container')
      if (!searchResults) {
        setTimeout(findGooglePlacesSearchResultsElement, 100)
        return
      }
      googlePlacesSearchResultsRef.value = searchResults
      googlePlacesSearchResultsRef.value.setAttribute('role', 'listbox')
      googlePlacesSearchResultsRef.value.setAttribute('id', 'googlePlacesSearchResults')
      observer.observe(googlePlacesSearchResultsRef.value, { attributes: true, childList: true })
    }

    onMounted(async () => {
      initGooglePlacesAutocomplete(googlePlacesSearchField.value, filterCentersAndZoom)
      findGooglePlacesSearchResultsElement()
    })

    const showFilteredResults = computed(() => {
      let show = false
      if (centerInformationFiltered.value && centerInformationFiltered.value.length === 3) {
        show = true
      }
      return show
    })

    // controlling focus points and pins data

    const filterCentersAndZoom = function (places) {
      showListMobileCount.value = 3
      const searchHasHits = !!places.getPlace().geometry
      if (searchHasHits) {
        placeSelected.value = places.getPlace()
        centerInformationFiltered.value = zoomMapToCenters(placeSelected.value, centerInformationComplete.value)
        focusPoints.value = setFocusPoints(centerInformationFiltered.value)
      } else {
        placeSelected.value = null
      }
    }

    const resetCenterSearch = function () {
      googlePlacesSearchField.value.value = ""
      centerInformationFiltered.value = centerInformationComplete.value
      focusPoints.value = setFocusPoints(centerInformationFiltered.value)
      showListMobileCount.value = 3
    }

    // handling category selection

    const applyFilters = function () {
      let filtered = centerInformationComplete.value

      // Category filter
      if (selectedCategories.value.length > 0) {
        filtered = filtered.filter((center) => {
          return center.categories.some((category) => selectedCategories.value.includes(category))
        })
      }

      // Bank filters
      if (selectedBanks.value.length === 0) {
        filtered = []
      } else {
        filtered = filtered.filter((center) => {
          if (selectedBanks.value.includes('nykredit') && !center.isSparNord) return true
          if (selectedBanks.value.includes('sparnord') && center.isSparNord) return true
          return false
        })
      }

      centerInformationFiltered.value = filtered

      if (centerInformationFiltered.value.length) {
        focusPoints.value = setFocusPoints(centerInformationFiltered.value)
      } else {
        focusPoints.value = []
      }
    }

    const filterCategories = function (userSelection) {
      showListMobileCount.value = 3
      const selectedCategoryIds = userSelection.filter((option) => option.selected).map((option) => parseInt(option.value))
      selectedCategories.value = selectedCategoryIds
      applyFilters()
    }

    const filterBanks = function (userSelection) {
      showListMobileCount.value = 3
      selectedBanks.value = userSelection.filter((option) => option.selected).map((option) => option.value)
      applyFilters()
    }

    const getDistance = (info) => {
      if (!placeSelected.value) return
      const distance = getDistanceFromLatLonInKm(placeSelected.value, info)
      return `${distance.toFixed(2)} km`
    }

    const handleShowMapMobile = () => {
      showMapsMobile.value = true
    }

    const handleShowListResultsMobile = () => {
      showMapsMobile.value = false
    }

    const handleShowAllListMobile = () => {
      showListMobileCount.value = 10000
    }

    const updateCurrentZoom = (zoom) => {
      currentZoom.value = zoom
    }

    return {
      centerInformationFiltered,
      filterCategories,
      filterBanks,
      focusPoints,
      googlePlacesSearchField,
      multiSelectCategoriesOptions,
      placeSearchQuery,
      showFilteredResults,
      resetCenterSearch,
      getDistance,
      showMapsMobile,
      handleShowMapMobile,
      handleShowListResultsMobile,
      showListMobileCount,
      handleShowAllListMobile,
      googlePlacesSearchResultsShown,
      updateCurrentZoom,
      currentZoom
    }
  },
  components: {
    GoggleMapsWithMarkers,
    MultiSelectDropdown,
  },
}
